import { toEnglishDigits } from 'utilities/helper/toEnglishNumber'

/**
 * Ordering and search for «صندوق‌هایی که مدیر آن هستید».
 *
 * The order a manager drags into is **client-only** on purpose: it lives in
 * this browser's localStorage, not on the fund. That is what makes the feature
 * cheap enough to ship for feedback — no endpoint, no migration, no
 * per-poolship column — and it is also why the merge below has to be
 * defensive. The stored value is a list of ids from an arbitrarily old session:
 * funds in it may have been left, and funds not in it may have been joined
 * since.
 */

const STORAGE_KEY = 'home:managed-funds-order'

/**
 * The order used before anyone has dragged anything, and the tie-breaker for
 * funds the saved order has never seen.
 *
 * Funds you OWN come first, then ones you merely manage; within each, the
 * biggest fund first. Ownership outranks size because it is where the
 * decisions actually sit, and member count is the best proxy we have here for
 * "which of these matters most".
 */
export function defaultOrder(funds: MoneyPool[]): MoneyPool[] {
    const rank = (mp: MoneyPool) => (mp.poolship?.role === 'own' ? 0 : 1)
    return [...funds].sort((a, b) => {
        if (rank(a) !== rank(b)) return rank(a) - rank(b)
        return (b.active_members_count ?? 0) - (a.active_members_count ?? 0)
    })
}

export function readSavedOrder(): number[] {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        const parsed = raw ? JSON.parse(raw) : null
        if (!Array.isArray(parsed)) return []
        return parsed.filter((id): id is number => typeof id === 'number')
    } catch {
        return []
    }
}

export function saveOrder(ids: number[]): void {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids))
    } catch {
        // Private-mode Safari and a full quota both throw here. Losing the
        // preference is not worth blowing up the home page over.
    }
}

/**
 * Merge the saved order with the funds the API actually returned.
 *
 * Funds the saved order has never seen go **first**, in default order. That
 * looks backwards until you remember the list is collapsed to three rows: a
 * fund you joined or created today, appended to the bottom of a manager's
 * hand-sorted list of nine, would be invisible behind «۶ صندوق دیگر». Putting
 * it on top costs one drag to undo and cannot hide anything.
 *
 * Ids in the saved order that no longer resolve to a fund are dropped, and the
 * component rewrites the whole list on the next drag, so stale ids do not
 * accumulate.
 */
export function applySavedOrder(
    funds: MoneyPool[],
    savedIds: number[]
): MoneyPool[] {
    const ranked = defaultOrder(funds)
    if (savedIds.length === 0) return ranked

    const position = new Map(savedIds.map((id, index) => [id, index]))
    const known: MoneyPool[] = []
    const fresh: MoneyPool[] = []
    ranked.forEach((mp) => (position.has(mp.id) ? known : fresh).push(mp))
    known.sort((a, b) => (position.get(a.id) ?? 0) - (position.get(b.id) ?? 0))
    return [...fresh, ...known]
}

/**
 * Fold away the spelling differences that make Persian search feel broken:
 * Arabic yeh/kaf typed by iOS keyboards, ZWNJ inside compound words
 * («قرض‌الحسنه» vs «قرض الحسنه»), alef with hamza, heh-yeh, tatweel, and
 * Persian/Arabic digits.
 */
export function normalizeFa(text: string): string {
    return toEnglishDigits(text)
        .replace(/[يى]/g, 'ی')
        .replace(/ك/g, 'ک')
        .replace(/[أإآ]/g, 'ا')
        .replace(/[ۀة]/g, 'ه')
        .replace(/[ً-ْـ]/g, '') // harakat + tatweel
        .replace(/[‌‏‎]/g, ' ') // ZWNJ and bidi marks
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim()
}

/**
 * Every whitespace-separated token of the query must appear somewhere in the
 * name, in any order — so «خانوادگی قرض» finds «صندوق قرض‌الحسنه خانوادگی».
 */
export function matchesQuery(fund: MoneyPool, query: string): boolean {
    const tokens = normalizeFa(query).split(' ').filter(Boolean)
    if (tokens.length === 0) return true
    const name = normalizeFa(fund.name ?? '')
    return tokens.every((token) => name.includes(token))
}
